import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
    CreditCardIcon, 
    CheckCircleIcon, 
    ExclamationTriangleIcon,
    ShieldCheckIcon,
    UsersIcon,
    BanknotesIcon,
    GlobeAmericasIcon
} from '@heroicons/react/24/outline';

interface Package {
    _id: string;
    name: string;
    priceUSD: number;
    priceAUD: number;
    lastMinuteDealPriceUSD?: number;
    lastMinuteDealPriceAUD?: number;
    lastMinuteDealExpiryDate?: string;
    hasActiveLastMinuteDeal?: boolean;
    description: string;
    packageCode: string;
    nights: number;
    actionType: 'enquire' | 'book';
    enquireEnabled: boolean;
    bookEnabled: boolean;
}

declare global {
    interface Window {
        FlywirePayment: any;
    }
}

const PayNowPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const packageId = searchParams.get('package');
    const priceType = searchParams.get('priceType') as 'standard' | 'deal' || 'standard';
    const [packages, setPackages] = useState<Package[]>([]);
    const [selectedPackageId, setSelectedPackageId] = useState<string>(packageId || '');
    const [selectedPriceType, setSelectedPriceType] = useState<'standard' | 'deal'>(priceType);
    const [manualAmount, setManualAmount] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [paymentCapturing, setPaymentCapturing] = useState(false);
    const [showLoadingBeforePayPal, setShowLoadingBeforePayPal] = useState(false);
    const [error, setError] = useState<string>('');
    const [settings, setSettings] = useState<any>(null);
    const [currency, setCurrency] = useState<'USD' | 'AUD'>('AUD');
    const [isManualPayment, setIsManualPayment] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'flywire' | null>(null);
    const [showPayPalButtons, setShowPayPalButtons] = useState(false);

    // Customer details
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        countryCode: '+61',
        country: 'AU'
    });

    // Fetch settings and packages
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const cachedSettings = localStorage.getItem('baligetaway-settings');
                const cacheTime = localStorage.getItem('baligetaway-settings-time');
                const fiveMinutes = 5 * 60 * 1000;

                if (cachedSettings && cacheTime && (Date.now() - parseInt(cacheTime)) < fiveMinutes) {
                    console.log('Using cached settings');
                    const parsedSettings = JSON.parse(cachedSettings);
                    setSettings(parsedSettings);
                    // Auto-select payment method if only one is enabled
                    autoSelectPaymentMethod(parsedSettings);
                    return;
                }

                const response = await fetch('/api/settings/public');
                if (response.ok) {
                    const settingsData = await response.json();
                    setSettings(settingsData);
                    localStorage.setItem('baligetaway-settings', JSON.stringify(settingsData));
                    localStorage.setItem('baligetaway-settings-time', Date.now().toString());
                    autoSelectPaymentMethod(settingsData);
                }
            } catch (error) {
                console.error('Error fetching settings:', error);
            }
        };

        const fetchPackages = async () => {
            try {
                const response = await axios.get('/api/packages?live=true');
                const allPackages = response.data.packages || [];
                const bookablePackages = allPackages.filter((pkg: Package) =>
                    pkg.bookEnabled && !(pkg.actionType === 'enquire' && !pkg.bookEnabled)
                );
                setPackages(bookablePackages);

                if (packageId) {
                    const selectedPkg = allPackages.find((pkg: Package) => pkg._id === packageId);
                    if (selectedPkg && selectedPkg.bookEnabled) {
                        setSelectedPackageId(packageId);
                    }
                }

                if (bookablePackages.length === 0) {
                    setIsManualPayment(true);
                }
            } catch (error) {
                console.error('Error fetching packages:', error);
                setIsManualPayment(true);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
        fetchPackages();
    }, [packageId]);

    const autoSelectPaymentMethod = (settingsData: any) => {
        const paypalEnabled = settingsData?.paypal?.enabled;
        const flywireEnabled = settingsData?.flywire?.enabled ?? true;

        if (paypalEnabled && !flywireEnabled) {
            setPaymentMethod('paypal');
        } else if (flywireEnabled && !paypalEnabled) {
            setPaymentMethod('flywire');
        }
        // If both enabled, user must choose
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const getPaymentAmount = () => {
        if (isManualPayment) return parseFloat(manualAmount) || 0;

        const selectedPackage = packages.find(p => p._id === selectedPackageId);
        if (!selectedPackage) return 0;

        if (selectedPriceType === 'deal') {
            const dealPriceAUD = selectedPackage.lastMinuteDealPriceAUD;
            const dealPriceUSD = selectedPackage.lastMinuteDealPriceUSD;
            const hasDealPrice = currency === 'AUD'
                ? dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < selectedPackage.priceAUD
                : dealPriceUSD && dealPriceUSD > 0 && dealPriceUSD < selectedPackage.priceUSD;
            const isDealActive = selectedPackage.lastMinuteDealExpiryDate
                ? new Date(selectedPackage.lastMinuteDealExpiryDate) > new Date()
                : false;

            if (hasDealPrice && isDealActive) {
                return currency === 'USD'
                    ? (dealPriceUSD || selectedPackage.priceUSD)
                    : (dealPriceAUD || selectedPackage.priceAUD);
            }
        }

        return currency === 'USD' ? selectedPackage.priceUSD : selectedPackage.priceAUD;
    };

    const validateForm = () => {
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
            setError('Please fill in all required fields');
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            return false;
        }

        if (formData.phone.length < 8) {
            setError('Please enter a valid phone number');
            return false;
        }

        const amount = getPaymentAmount();
        if (amount <= 0) {
            setError('Please enter a valid amount or select a package');
            return false;
        }

        return true;
    };

    const getPackageInfo = () => {
        if (!isManualPayment && selectedPackageId) {
            return packages.find(p => p._id === selectedPackageId);
        }
        return null;
    };

    // PayPal handlers
    const createPayPalOrder = async () => {
        const amount = getPaymentAmount();
        const packageInfo = getPackageInfo();
        const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '');

        console.log('💰 Creating PayPal order with currency:', currency, 'Amount:', amount);

        try {
            const response = await axios.post('/api/paypal/create-order', {
                amount,
                currency,
                packageId: isManualPayment ? null : selectedPackageId,
                packageName: packageInfo ? packageInfo.name : 'Custom Payment',
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: `${formData.countryCode}${cleanPhone}`
            });

            if (response.data.success) {
                return response.data.orderId;
            } else {
                throw new Error(response.data.message);
            }
        } catch (err: any) {
            console.error('PayPal create order error:', err);
            setError(err.response?.data?.message || 'Failed to create PayPal order');
            throw err;
        }
    };

    const onPayPalApprove = async (data: any) => {
        try {
            setPaymentCapturing(true); // Show full-screen loading overlay
            setProcessing(true);
            const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '');
            const packageInfo = getPackageInfo();

            const response = await axios.post('/api/paypal/capture-order', {
                orderId: data.orderID,
                customerData: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phone: `${formData.countryCode}${cleanPhone}`,
                    packageId: isManualPayment ? null : selectedPackageId,
                    packageName: packageInfo ? packageInfo.name : 'Custom Payment'
                }
            });

            if (response.data.success) {
                // Keep loading overlay visible during redirect
                navigate(`/payment-success?status=success&reference=${response.data.bookingCode}&transactionId=${response.data.transactionId}&amount=${response.data.amount}&currency=${response.data.currency}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&package=${encodeURIComponent(packageInfo?.name || 'Custom Payment')}&paymentMethod=PayPal`);
            } else {
                setPaymentCapturing(false);
                setError(response.data.message || 'Payment capture failed');
            }
        } catch (err: any) {
            console.error('PayPal capture error:', err);
            setPaymentCapturing(false);
            setError(err.response?.data?.message || 'Failed to capture payment');
        } finally {
            setProcessing(false);
        }
    };

    // Flywire handler (same as BookNowPage)
    const handleFlywirePayment = async () => {
        if (!validateForm()) return;

        setProcessing(true);
        setError('');

        try {
            const amount = getPaymentAmount();
            const packageInfo = getPackageInfo();
            const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '');

            if (typeof window.FlywirePayment === 'undefined') {
                setError('Flywire payment system not loaded. Please refresh the page.');
                setProcessing(false);
                return;
            }

            const isLive = settings?.flywire?.environment === 'live';
            const recipientCode = isLive
                ? (currency === 'AUD' ? 'FQD' : 'FQG')
                : 'IRG';

            const transactionId = `TXN${Date.now()}`;
            const returnUrl = `${window.location.origin}/payment-success?status=pending&reference=${transactionId}&transactionId=${transactionId}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageInfo?.name || 'Custom Payment')}&packageId=${selectedPackageId || ''}&phone=${encodeURIComponent(formData.countryCode + cleanPhone)}&paymentMethod=Flywire`;
            const callbackUrl = `${window.location.origin}/api/payment/webhook/${currency.toLowerCase()}`;

            const flywireConfig = {
                env: isLive ? 'prod' : 'demo',
                recipientCode,
                amount: parseFloat(amount.toFixed(2)),
                currency: currency.toUpperCase(),
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim().toLowerCase(),
                phone: `${formData.countryCode}${cleanPhone}`,
                recipientFields: {
                    booking_reference: transactionId,
                    web_source: window.location.hostname,
                    package_name: packageInfo?.name || 'Custom Payment',
                    package_code: packageInfo?.packageCode || 'CUSTOM'
                },
                requestPayerInfo: true,
                requestRecipientInfo: false,
                returnUrl,
                callbackUrl,
                callbackId: "BaliGetaway",
                callbackVersion: "2",
                onInvalidInput: (errors: any[]) => {
                    const errorMessages = errors.map(error => error.msg || error.message).join('\n');
                    setError(`Payment validation failed:\n${errorMessages}`);
                    setProcessing(false);
                },
                // Handle successful payment completion
                onSuccess: async (result: any) => {
                    console.log('✅ Flywire payment success:', result);
                    try {
                        // Extract Flywire payment ID from result
                        const flywirePaymentId = result?.paymentId || result?.payment_id || result?.id || transactionId;
                        
                        // Create booking record immediately
                        const bookingResponse = await axios.post('/api/bookings/create', {
                            firstName: formData.firstName,
                            lastName: formData.lastName,
                            email: formData.email,
                            phone: `${formData.countryCode}${cleanPhone}`,
                            amount: amount,
                            currency: currency,
                            packageId: isManualPayment ? null : selectedPackageId,
                            packageName: packageInfo?.name || 'Custom Payment',
                            transactionId: flywirePaymentId,
                            paymentMethod: 'flywire'
                        });
                        
                        console.log('✅ Booking created:', bookingResponse.data);
                        
                        // Redirect to success page with actual Flywire payment ID
                        const successUrl = `/payment-success?status=success&transactionId=${flywirePaymentId}&reference=${bookingResponse.data.booking?.bookingCode || transactionId}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageInfo?.name || 'Custom Payment')}&paymentMethod=Flywire`;
                        navigate(successUrl);
                    } catch (bookingError: any) {
                        console.error('❌ Failed to create booking after payment:', bookingError);
                        // Still redirect to success page even if booking creation fails
                        const successUrl = `/payment-success?status=success&transactionId=${transactionId}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageInfo?.name || 'Custom Payment')}&paymentMethod=Flywire`;
                        navigate(successUrl);
                    }
                },
                // Handle payment completion (fallback)
                onComplete: async (result: any) => {
                    console.log('✅ Flywire payment complete:', result);
                    // onSuccess should handle this, but as fallback:
                    if (result?.status === 'success' || result?.status === 'completed') {
                        const flywirePaymentId = result?.paymentId || result?.payment_id || result?.id || transactionId;
                        try {
                            await axios.post('/api/bookings/create', {
                                firstName: formData.firstName,
                                lastName: formData.lastName,
                                email: formData.email,
                                phone: `${formData.countryCode}${cleanPhone}`,
                                amount: amount,
                                currency: currency,
                                packageId: isManualPayment ? null : selectedPackageId,
                                packageName: packageInfo?.name || 'Custom Payment',
                                transactionId: flywirePaymentId,
                                paymentMethod: 'flywire'
                            });
                        } catch (e) {
                            console.error('Booking creation in onComplete failed:', e);
                        }
                    }
                },
                // Handle payment cancellation
                onCancel: () => {
                    console.log('❌ Flywire payment cancelled by user');
                    setError('Payment was cancelled. Please try again when ready.');
                    setProcessing(false);
                }
            };

            const modal = window.FlywirePayment.initiate(flywireConfig);
            if (modal) {
                modal.render();
                setProcessing(false);
            } else {
                throw new Error('Failed to initiate payment modal');
            }
        } catch (err: any) {
            console.error('Flywire error:', err);
            setError(err.message || 'Failed to initiate payment');
            setProcessing(false);
        }
    };

    const handleProceedToPayment = async () => {
        if (!validateForm()) return;

        if (paymentMethod === 'paypal') {
            // Show loading animation first
            setShowLoadingBeforePayPal(true);
            // Wait 1.5 seconds for loading animation, then show PayPal buttons
            setTimeout(() => {
                setShowLoadingBeforePayPal(false);
                setShowPayPalButtons(true);
            }, 1500);
        } else if (paymentMethod === 'flywire') {
            handleFlywirePayment();
        } else {
            setError('Please select a payment method');
        }
    };

    const shouldShowPackageTab = () => packages.length > 0;
    const shouldShowBothTabs = () => packages.length > 0;

    const paypalEnabled = settings?.paypal?.enabled;
    const flywireEnabled = settings?.flywire?.enabled ?? true;
    const bothEnabled = paypalEnabled && flywireEnabled;

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-orange-600 mx-auto mb-4"></div>
                    <p className="text-gray-700 text-lg">Loading payment options...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Pay Now - Secure Payment | Bali Getaway</title>
                <meta name="description" content="Complete your booking with secure payment processing" />
            </Helmet>

            {/* Payment Capturing Overlay */}
            {paymentCapturing && (
                <div className="fixed inset-0 z-50 bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                    <div className="text-center text-white p-8">
                        <div className="relative mb-8">
                            <div className="w-32 h-32 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        </div>
                        <h2 className="text-3xl font-bold mb-4">Processing Your Payment</h2>
                        <p className="text-xl text-white/90 mb-2">Please wait while we confirm your payment...</p>
                        <p className="text-lg text-white/70">This usually takes just a few seconds</p>
                        <div className="mt-8 flex justify-center gap-2">
                            <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                    </div>
                </div>
            )}

            <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 py-12">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        {/* Header */}
                        <div className="text-center mb-12">
                            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                                Complete Your <span className="bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">Payment</span>
                            </h1>
                            <p className="text-xl text-gray-700">Secure payment processing</p>
                        </div>

                        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                            <div className="p-8 lg:p-12">
                                {/* Error Message */}
                                {error && (
                                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                                        <ExclamationTriangleIcon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-semibold text-red-800">Error</p>
                                            <p className="text-red-700 text-sm">{error}</p>
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-8">
                                    {/* Payment Type Selection */}
                                    {shouldShowBothTabs() && (
                                        <div className="space-y-4">
                                            <label className="block text-lg font-bold text-gray-900 ml-1">
                                                Select Payment Type
                                            </label>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsManualPayment(false)}
                                                    className={`p-6 rounded-3xl border-2 transition-all flex flex-col items-center gap-3 hover:shadow-md text-center ${!isManualPayment
                                                        ? 'border-orange-500 bg-orange-50 shadow-sm ring-4 ring-orange-500/10'
                                                        : 'border-gray-100 bg-white hover:border-orange-200'
                                                        }`}
                                                >
                                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${!isManualPayment ? 'bg-white shadow-sm' : 'bg-gray-50'}`}>
                                                        <span className="text-3xl">🎁</span>
                                                    </div>
                                                    <div>
                                                        <span className="block font-bold text-lg text-gray-900">Book Package</span>
                                                        <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Choose a holiday package</span>
                                                    </div>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setIsManualPayment(true)}
                                                    className={`p-6 rounded-3xl border-2 transition-all flex flex-col items-center gap-3 hover:shadow-md text-center ${isManualPayment
                                                        ? 'border-orange-500 bg-orange-50 shadow-sm ring-4 ring-orange-500/10'
                                                        : 'border-gray-100 bg-white hover:border-orange-200'
                                                        }`}
                                                >
                                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${isManualPayment ? 'bg-white shadow-sm' : 'bg-gray-50'}`}>
                                                        <span className="text-3xl">💳</span>
                                                    </div>
                                                    <div>
                                                        <span className="block font-bold text-lg text-gray-900">Custom Payment</span>
                                                        <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Pay for special extras</span>
                                                    </div>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Package Selection or Manual Amount */}
                                    <div className="space-y-4">
                                        {shouldShowPackageTab() && !isManualPayment ? (
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                                                    Select Package *
                                                </label>
                                                <select
                                                    value={selectedPackageId}
                                                    onChange={(e) => setSelectedPackageId(e.target.value)}
                                                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 font-bold"
                                                >
                                                    <option value="">Choose a package...</option>
                                                    {packages.filter(pkg => pkg.bookEnabled).map((pkg) => (
                                                        <option key={pkg._id} value={pkg._id}>
                                                            {pkg.name} - {currency} {(currency === 'USD' ? pkg.priceUSD : pkg.priceAUD).toFixed(2)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                                                    Enter Amount ({currency}) *
                                                </label>
                                                <input
                                                    type="number"
                                                    value={manualAmount}
                                                    onChange={(e) => setManualAmount(e.target.value)}
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="0.01"
                                                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-black text-xl"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Customer Details */}
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-2 text-gray-900 font-bold border-b border-gray-100 pb-2">
                                            <UsersIcon className="w-5 h-5 text-orange-500" />
                                            <h3>Guest Information</h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                                                    First Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="firstName"
                                                    value={formData.firstName}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                                                    placeholder="John"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                                                    Last Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    value={formData.lastName}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                                                    placeholder="Doe"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                                                placeholder="john@example.com"
                                            />
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                                                Phone Number *
                                            </label>
                                            <div className="grid grid-cols-3 gap-3">
                                                <select
                                                    name="countryCode"
                                                    value={formData.countryCode}
                                                    onChange={handleInputChange}
                                                    className="px-3 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 font-bold"
                                                >
                                                    <option value="+61">🇦🇺 +61</option>
                                                    <option value="+1">🇺🇸 +1</option>
                                                    <option value="+44">🇬🇧 +44</option>
                                                    <option value="+91">🇮🇳 +91</option>
                                                </select>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    className="col-span-2 px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                                                    placeholder="412 345 678"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Payment Method Selection - Visual Cards */}
                                    {bothEnabled && !showPayPalButtons && !showLoadingBeforePayPal && (
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2 text-gray-900 font-bold border-b border-gray-100 pb-2">
                                                <BanknotesIcon className="w-5 h-5 text-orange-500" />
                                                <h3>Select Payment Method</h3>
                                            </div>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <button
                                                    onClick={() => setPaymentMethod('paypal')}
                                                    className={`p-6 rounded-3xl border-2 transition-all flex flex-col items-center gap-3 hover:shadow-md text-center ${paymentMethod === 'paypal'
                                                        ? 'border-orange-500 bg-orange-50 shadow-sm ring-4 ring-orange-500/10'
                                                        : 'border-gray-100 bg-white hover:border-orange-200'
                                                        }`}
                                                >
                                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${paymentMethod === 'paypal' ? 'bg-white shadow-sm' : 'bg-gray-50'}`}>
                                                        <span className="text-3xl">💳</span>
                                                    </div>
                                                    <div>
                                                        <span className="block font-bold text-lg text-gray-900">PayPal</span>
                                                        <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Debit / Credit Card / Wallet</span>
                                                    </div>
                                                </button>

                                                <button
                                                    onClick={() => setPaymentMethod('flywire')}
                                                    className={`p-6 rounded-3xl border-2 transition-all flex flex-col items-center gap-3 hover:shadow-md text-center ${paymentMethod === 'flywire'
                                                        ? 'border-orange-500 bg-orange-50 shadow-sm ring-4 ring-orange-500/10'
                                                        : 'border-gray-100 bg-white hover:border-orange-200'
                                                        }`}
                                                >
                                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${paymentMethod === 'flywire' ? 'bg-white shadow-sm' : 'bg-gray-50'}`}>
                                                        <span className="text-3xl">🌐</span>
                                                    </div>
                                                    <div>
                                                        <span className="block font-bold text-lg text-gray-900">Flywire</span>
                                                        <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">International Bank Transfer</span>
                                                    </div>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Loading Animation Before PayPal */}
                                    {showLoadingBeforePayPal && (
                                        <div className="py-12 text-center">
                                            <div className="relative mb-6">
                                                <div className="w-20 h-20 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mx-auto"></div>
                                            </div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Preparing Payment...</h3>
                                            <p className="text-gray-600">Loading PayPal checkout</p>
                                        </div>
                                    )}

                                    {/* Payment Summary */}
                                    {getPaymentAmount() > 0 && (
                                        <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-8 rounded-[2rem] border border-orange-100 shadow-sm relative overflow-hidden group">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/40 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                                            <div className="flex justify-between items-center relative z-10">
                                                <div>
                                                    <p className="text-xs font-black text-orange-400 uppercase tracking-widest mb-1">Total Amount Due</p>
                                                    <span className="text-4xl font-black text-orange-600">
                                                        {currency} {getPaymentAmount().toFixed(2)}
                                                    </span>
                                                </div>
                                                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                                                    <ShieldCheckIcon className="w-6 h-6 text-orange-500" />
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 text-[10px] font-bold text-orange-400/80 uppercase tracking-tighter mt-6 bg-white/50 w-fit px-3 py-1 rounded-full border border-orange-100/50">
                                                <CheckCircleIcon className="w-3.5 h-3.5" />
                                                <span>Encrypted & Secure Transaction</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* PayPal Buttons */}
                                    {showPayPalButtons && settings?.paypal?.clientId && (
                                        <motion.div 
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="space-y-6 pt-4"
                                        >
                                            <button 
                                                onClick={() => setShowPayPalButtons(false)}
                                                className="text-orange-600 hover:text-orange-700 text-sm font-black flex items-center gap-2 group transition-all"
                                            >
                                                <span className="group-hover:-translate-x-1 transition-transform font-black">←</span>
                                                CHANGE PAYMENT METHOD
                                            </button>
                                            <div className="bg-gray-50 p-8 rounded-[2.5rem] border border-gray-100 shadow-inner">
                                                <PayPalScriptProvider options={{
                                                    clientId: settings.paypal.clientId,
                                                    currency: currency,
                                                    intent: 'capture'
                                                }}>
                                                    <PayPalButtons
                                                        style={{ layout: 'vertical', shape: 'rect', label: 'pay' }}
                                                        createOrder={createPayPalOrder}
                                                        onApprove={onPayPalApprove}
                                                        onError={(err) => {
                                                            console.error('PayPal error:', err);
                                                            setError('PayPal payment failed. Please try again.');
                                                        }}
                                                        onCancel={() => {
                                                            setShowPayPalButtons(false);
                                                            setError('Payment cancelled.');
                                                        }}
                                                    />
                                                </PayPalScriptProvider>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Submit Button */}
                                    {!showPayPalButtons && !showLoadingBeforePayPal && (
                                        <button
                                            type="button"
                                            onClick={handleProceedToPayment}
                                            disabled={processing || getPaymentAmount() <= 0}
                                            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-black py-5 px-8 rounded-2xl hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-2xl shadow-orange-200 disabled:opacity-50 active:scale-[0.98] flex items-center justify-center gap-3 mt-4 tracking-wider uppercase"
                                        >
                                            {processing ? (
                                                <>
                                                    <svg className="animate-spin h-6 w-6 text-white" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                    <span>INITIALIZING...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <CreditCardIcon className="w-6 h-6" />
                                                    <span>PROCEED TO SECURE PAYMENT</span>
                                                </>
                                            )}
                                        </button>
                                    )}

                                    <p className="text-center text-sm text-gray-600">
                                        By proceeding, you agree to our Terms of Service and Privacy Policy
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PayNowPage;
